import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { UserIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

interface EditUsernameDialogProps {
    isOpen: boolean;
    currentUsername: string;
    onClose: () => void;
    onConfirm: (newUsername: string) => Promise<void>;
}

const EditUsernameDialog: React.FC<EditUsernameDialogProps> = ({
    isOpen,
    currentUsername,
    onClose,
    onConfirm
}) => {
    const { t } = useTranslation();
    const [newUsername, setNewUsername] = useState(currentUsername);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!newUsername.trim()) {
            setError(t('profile.editUsername.empty'));
            return;
        }
        if (newUsername.length < 2 || newUsername.length > 20) {
            setError(t('profile.editUsername.lengthError'));
            return;
        }
        if (newUsername === currentUsername) {
            onClose();
            return;
        }

        setIsSubmitting(true);
        try {
            await onConfirm(newUsername);
        } catch (error) {
            setError(t('profile.editUsername.updateFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-indigo-900/70 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white/10 backdrop-blur-xl px-4 pb-4 pt-5 text-left shadow-2xl transition-all border border-white/20 sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div>
                                    <motion.div 
                                        className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/30 border border-blue-300/50 shadow-lg"
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <UserIcon className="h-8 w-8 text-blue-100" aria-hidden="true" />
                                    </motion.div>
                                    <div className="mt-4 text-center">
                                        <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-white">
                                            {t('profile.editUsername.title')}
                                        </Dialog.Title>
                                        <div className="mt-6">
                                            <div className="relative rounded-lg shadow-lg">
                                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                    <UserIcon className="h-5 w-5 text-blue-300" aria-hidden="true" />
                                                </div>
                                                <input
                                                    type="text"
                                                    value={newUsername}
                                                    onChange={(e) => {
                                                        setNewUsername(e.target.value);
                                                        setError('');
                                                    }}
                                                    className={`block w-full rounded-lg border-0 py-3 pl-10 text-white bg-white/10 backdrop-blur-sm ring-1 ring-inset ${
                                                        error 
                                                            ? 'ring-red-400 placeholder:text-red-200 focus:ring-red-400' 
                                                            : 'ring-blue-300/50 placeholder:text-blue-200/70 focus:ring-blue-300'
                                                    } focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                                                    placeholder={t('profile.editUsername.placeholder')}
                                                    autoFocus
                                                />
                                            </div>
                                            {error && (
                                                <motion.p 
                                                    className="mt-3 text-sm text-red-200"
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.2 }}
                                                >
                                                    {error}
                                                </motion.p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-8 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-4">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-full bg-blue-500 bg-opacity-80 px-4 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 sm:col-start-2"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? t('common.saving') : t('common.confirm')}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-full bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm shadow-lg ring-1 ring-inset ring-white/20 hover:bg-white/20 transition-all duration-200 sm:col-start-1 sm:mt-0"
                                        onClick={onClose}
                                        disabled={isSubmitting}
                                    >
                                        {t('common.cancel')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default EditUsernameDialog; 